/**
 * CBAResultsPage - Cost-Benefit Analysis results visualization.
 *
 * Props:
 *   - scenarioId: UUID of the scenario (for URL routing)
 *   - params: CBA parameters (optional - uses mock if not provided)
 *
 * Displays:
 *   1. Scenario Comparison Cards (No Intervention, PV Only, PV+BESS)
 *   2. Cumulative Cash Flow Chart
 *   3. Annual Cost Breakdown Chart
 *   4. Sensitivity Analysis Table
 *   5. CO2 Impact Card
 *   6. Summary Recommendation Card
 *
 * Data sourcing:
 *   - Calls /v1/cba/calculate on mount via useCalculateCBA().
 *   - On API error (e.g. cba-api not yet implemented) falls back to
 *     czechCBAResults() mock so the UI remains functional.
 */

import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { czechCBAResults } from '../data/czechEnergyProfiles'
import { useCalculateCBA } from '../hooks/useCBA'
import { resultsAPI } from '../api/results'
import { exportCBAResults } from '../utils/exportCSV'
import { getCountryDefaults } from '../data/countryDefaults'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'

// EUR -> CZK exchange rate used for display conversion.
const EUR_TO_CZK = 25

/**
 * Map a raw backend CBA response to the shape that the chart/card
 * rendering code expects (same structure as czechCBAResults()).
 *
 * Backend response (from /v1/cba/calculate) is expected to include:
 *   scenarios[]: { name, npv_eur, bcr, payback_years, annual_savings_eur, co2_avoided_kg_yr }
 *   cumulative_cash_flow: { years[], baseline[], pv_only[], pv_bess[] }
 *   annual_cost_breakdown: { categories[], pv_bess: { grid_import[], pv_value[], battery_value[], om[] } }
 *   sensitivity: { discount_rates[], pv_only[], pv_bess[] }
 *
 * If the backend uses different key names we fall back to mock data, so
 * this mapping is best-effort and defensive.
 */
function mapBackendResponse(raw, isCZK) {
  const rate = isCZK ? EUR_TO_CZK : 1

  // --- scenarios ---
  const scenarios = (raw.scenarios || []).map((s) => ({
    name: s.name,
    npv: Math.round((s.npv_eur ?? s.npv ?? 0) * rate),
    bcr: s.bcr ?? 1,
    payback: s.payback_years ?? s.payback ?? null,
    annualSavings: Math.round((s.annual_savings_eur ?? s.annualSavings ?? 0) * rate),
    // co2_avoided may be in kg/yr — convert to t/yr; backend might already send tonnes
    co2Avoided: s.co2_avoided_tonnes_yr ?? (s.co2_avoided_kg_yr != null ? s.co2_avoided_kg_yr / 1000 : s.co2Avoided ?? 0),
    lifetimeCO2Avoided: s.lifetimeCO2Avoided ?? s.lifetime_co2_avoided ?? null,
    equivalentTreesPlanted: s.equivalentTreesPlanted ?? s.equivalent_trees_planted ?? null,
  }))

  // --- cumulative cash flow ---
  const ccf = raw.cumulative_cash_flow || raw.cumulativeCashFlow || {}
  const cumulativeCashFlow = {
    years: ccf.years ?? [],
    baseline: (ccf.baseline ?? ccf.noIntervention ?? ccf.no_intervention ?? []).map((v) => Math.round(v * rate)),
    pvOnly: (ccf.pvOnly ?? ccf.pv_only ?? ccf.communityPvOnly ?? []).map((v) => Math.round(v * rate)),
    bessOnly: (ccf.bessOnly ?? ccf.bess_only ?? []).map((v) => Math.round(v * rate)),
    pvBess: (ccf.pvBess ?? ccf.pv_bess ?? ccf.pvBatteryBess ?? []).map((v) => Math.round(v * rate)),
  }

  // --- annual cost breakdown (per scenario, keyed by camelCase name) ---
  const acd = raw.annual_cost_breakdown || raw.annualCostBreakdown || {}
  const extractBreakdown = (...keys) => {
    const bd = keys.reduce((found, k) => found || acd[k], null) || {}
    return {
      capex: Math.round((bd.capex ?? 0) * rate),
      gridImport: (bd.grid_import ?? bd.gridImport ?? []).map((v) => Math.round(v * rate)),
      pvValue: (bd.pv_value ?? bd.pvValue ?? []).map((v) => Math.round(v * rate)),
      batteryValue: (bd.battery_value ?? bd.batteryValue ?? []).map((v) => Math.round(v * rate)),
      exportRevenue: (bd.export_revenue ?? bd.exportRevenue ?? []).map((v) => Math.round(v * rate)),
      om: (bd.om ?? []).map((v) => Math.round(v * rate)),
      replacementCost: (bd.replacement_cost ?? bd.replacementCost ?? []).map((v) => Math.round(v * rate)),
    }
  }
  const annualCostBreakdown = {
    categories: acd.categories ?? ['Year 1', 'Year 5', 'Year 10', 'Year 15', 'Year 20'],
    baseline: extractBreakdown('baseline', 'noIntervention', 'no_intervention'),
    pvOnly: extractBreakdown('pvOnly', 'pv_only', 'communityPvOnly'),
    bessOnly: extractBreakdown('bessOnly', 'bess_only'),
    pvBess: extractBreakdown('pvBess', 'pv_bess', 'pvBatteryBess'),
  }

  // --- sensitivity ---
  const sens = raw.sensitivity || {}
  const sensitivity = {
    discountRates: sens.discount_rates ?? sens.discountRates ?? [],
    pvOnly: (sens.pvOnly ?? sens.pv_only ?? sens.communityPvOnly ?? []).map((v) => Math.round(v * rate)),
    bessOnly: (sens.bessOnly ?? sens.bess_only ?? []).map((v) => Math.round(v * rate)),
    pvBess: (sens.pvBess ?? sens.pv_bess ?? sens.pvBatteryBess ?? []).map((v) => Math.round(v * rate)),
  }

  return { scenarios, cumulativeCashFlow, annualCostBreakdown, sensitivity }
}

function CBAResultsPage({ scenarioId, params, communityId: propCommunityId, currency: propCurrency }) {
  const { canEdit } = useAuth()
  const { t } = useTranslation()
  const currency = propCurrency || params?.currency || 'EUR'
  const sym = currency === 'CZK' ? 'CZK' : '€'
  const isCZK = currency === 'CZK'
  // Use communityId prop (not scenarioId) for resultsAPI calls
  const communityId = propCommunityId || null
  const fmtK = (v) => v != null ? `${(v / 1000).toFixed(0)}k ${sym}` : ''
  const fmtV = (v) => v != null ? `${v.toLocaleString()} ${sym}` : ''

  const cbaMutation = useCalculateCBA()
  const [results, setResults] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  // 'loading' | 'api' | 'mock'
  const [dataSource, setDataSource] = useState('loading')
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)  // null | 'success' | 'error'
  const [breakdownScenario, setBreakdownScenario] = useState(null)  // null = auto-select first investment scenario
  const [co2Scenario, setCo2Scenario] = useState(null)  // null = auto-select best

  const handleSaveResults = async () => {
    if (!results) return
    const saveId = communityId || scenarioId
    if (!saveId) return
    setIsSaving(true)
    setSaveStatus(null)
    try {
      await resultsAPI.save(saveId, {
        result_type: 'cba',
        name: `CBA Results - ${new Date().toLocaleDateString()}`,
        parameters: { ...params, scenario_id: scenarioId },
        result_data: results,
      })
      setSaveStatus('success')
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (error) {
      console.error('Failed to save CBA results:', error)
      setSaveStatus('error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExportCSV = () => {
    if (!results) return
    exportCBAResults(results, currency)
  }

  useEffect(() => {
    const runCBA = async () => {
      setIsLoading(true)
      try {
        // Country-aware defaults: use community country, then global fallbacks
        const country = params?.country || 'Czech Republic'
        const cd = getCountryDefaults(country)

        const analysisPeriod = params?.analysisPeriodYears || 20
        // Total capacity = existing (from community assets) + new (from CBA form)
        const existingPv = params?.existingPvKwp || 0
        const existingBatt = params?.existingBatteryKwh || 0
        const newPv = params?.newPvCapacityKwp || 0
        const newBatt = params?.newBatteryCapacityKwh || 0
        // pvSizeKwp (from PlanningResultsPage) is already the total
        const pvKwp = params?.pvSizeKwp || (existingPv + newPv) || 42
        const battKwh = params?.battCapacityKwh || (existingBatt + newBatt) || 0
        const annualLoad = params?.annualLoadKwh || 65000
        const discountRate = (params?.discountRate || params?.discountRatePct || 8) / 100
        const pvDegradationRate = (params?.pvAnnualDegradationPct != null ? params.pvAnnualDegradationPct : 0.5) / 100
        const priceEscalation = (params?.purchaseTariffAnnualIncreasePct != null ? params.purchaseTariffAnnualIncreasePct : 3) / 100
        const pvYield = params?.pvYieldKwhPerKwp || cd.pvYieldKwhPerKwp
        const co2Factor = params?.co2Factor || params?.gridCO2intensityKgPerKwh || cd.co2FactorKgPerKwh

        // Currency conversion: backend always works in EUR.
        // If params come from CBAParamsForm in CZK, convert to EUR first.
        const paramsCurrency = params?.currency || currency
        const toEur = paramsCurrency === 'CZK' ? (1 / EUR_TO_CZK) : 1

        // Tariffs: use params if provided (converting to EUR), then community metadata, then error
        const communityMeta = params?.communityMetadata || {}
        const metaToEur = params?.communityCurrency === 'CZK' ? (1 / EUR_TO_CZK) : 1
        const tariffImport = params?.tariffImportEur
          || (params?.electricityPurchasePricePerKwh ? params.electricityPurchasePricePerKwh * toEur : null)
          || (communityMeta.import_tariff ? communityMeta.import_tariff * metaToEur : null)
          || (() => { console.error('Community import tariff not configured'); return 0 })()
        const tariffExport = params?.tariffExportEur
          || (params?.feedInTariffPerKwh ? params.feedInTariffPerKwh * toEur : null)
          || (communityMeta.export_tariff ? communityMeta.export_tariff * metaToEur : null)
          || (() => { console.error('Community export tariff not configured'); return 0 })()

        // CAPEX: use params if provided (converting to EUR), otherwise use EUR defaults
        const pvUnitCost = params?.pvUnitCostEur
          || (params?.pvCapexPerKwp ? params.pvCapexPerKwp * toEur : null)
          || 833   // EUR/kWp
        const battUnitCost = params?.battUnitCostEur
          || (params?.battCapexPerKwh ? params.battCapexPerKwh * toEur : null)
          || 750  // EUR/kWh
        // CAPEX only on NEW capacity (existing is already paid for)
        const pvCapex = newPv * pvUnitCost
        const battCapex = newBatt > 0 ? newBatt * battUnitCost : 0
        const pvOpexEur = params?.pvOpexEurKwp
          || (params?.pvAnnualMaintenancePerKwp ? params.pvAnnualMaintenancePerKwp * toEur : null)
          || 12  // EUR/yr
        const battOpexEur = params?.battOpexEurKwh
          || (params?.battAnnualMaintenancePerKwh ? params.battAnnualMaintenancePerKwh * toEur : null)
          || 10  // EUR/yr
        // OPEX on total capacity (existing + new both need maintenance)
        const pvAnnualOpex = pvKwp * pvOpexEur
        const battAnnualOpex = battKwh > 0 ? battKwh * battOpexEur : 0

        // C5: Environmental externality cost (C_env in ENTSO-E methodology)
        const envCost = params?.environmentalCostEur
          ? params.environmentalCostEur * toEur
          : 0

        // Generation for each scenario tier
        const existingPvGen = existingPv * pvYield
        const totalPvGen = pvKwp * pvYield
        // OPEX for existing-only (No Intervention maintains existing assets)
        const existingPvOpex = existingPv > 0 ? existingPv * pvOpexEur : 0
        const existingBattOpex = existingBatt > 0 ? existingBatt * battOpexEur : 0

        // Build scenarios dynamically based on user's checkbox selection
        const selectedScenarios = params?.cbaScenarios || ['pv_only']
        const battKwhForBess = newBatt > 0 ? newBatt : 100  // Fallback for BESS scenarios
        const totalBattKwh = existingBatt + battKwhForBess
        const battCapexForBess = battKwhForBess * battUnitCost
        const totalPvGenForBess = pvKwp * pvYield
        const battAnnualOpexForBess = totalBattKwh * battOpexEur

        const cbaScenarios = [
          // Baseline is always included
          {
            name: 'Baseline',
            capex: 0,
            annual_opex: existingPvOpex + existingBattOpex,
            annual_load_kwh: annualLoad,
            annual_pv_generation_kwh: existingPvGen,
            battery_capacity_kwh: existingBatt,
            tariff_import_eur_kwh: tariffImport,
            tariff_export_eur_kwh: tariffExport,
            co2_factor_kg_kwh: co2Factor,
            pv_degradation_rate: pvDegradationRate,
          },
        ]

        if (selectedScenarios.includes('pv_only')) {
          cbaScenarios.push({
            name: 'PV Only',
            capex: pvCapex,
            annual_opex: pvAnnualOpex,
            annual_load_kwh: annualLoad,
            annual_pv_generation_kwh: totalPvGen,
            battery_capacity_kwh: existingBatt,
            tariff_import_eur_kwh: tariffImport,
            tariff_export_eur_kwh: tariffExport,
            co2_factor_kg_kwh: co2Factor,
            pv_degradation_rate: pvDegradationRate,
            environmental_cost: envCost,
          })
        }

        if (selectedScenarios.includes('bess_only')) {
          cbaScenarios.push({
            name: 'BESS Only',
            capex: battCapexForBess,
            annual_opex: existingPvOpex + battAnnualOpexForBess,
            annual_load_kwh: annualLoad,
            annual_pv_generation_kwh: existingPvGen,
            battery_capacity_kwh: totalBattKwh,
            tariff_import_eur_kwh: tariffImport,
            tariff_export_eur_kwh: tariffExport,
            co2_factor_kg_kwh: co2Factor,
            pv_degradation_rate: pvDegradationRate,
            environmental_cost: envCost,
            battery_capex: battCapexForBess,
            batt_lifetime_years: params?.battLifetimeYears || null,
            batt_replacement_cost_pct: params?.battReplacementCostPct || 80,
          })
        }

        if (selectedScenarios.includes('pv_bess')) {
          cbaScenarios.push({
            name: 'PV + BESS',
            capex: pvCapex + battCapexForBess,
            annual_opex: pvAnnualOpex + battAnnualOpexForBess,
            annual_load_kwh: annualLoad,
            annual_pv_generation_kwh: totalPvGenForBess,
            tariff_import_eur_kwh: tariffImport,
            tariff_export_eur_kwh: tariffExport,
            battery_capacity_kwh: totalBattKwh,
            co2_factor_kg_kwh: co2Factor,
            pv_degradation_rate: pvDegradationRate,
            environmental_cost: envCost,
            battery_capex: battCapexForBess,
            batt_lifetime_years: params?.battLifetimeYears || null,
            batt_replacement_cost_pct: params?.battReplacementCostPct || 80,
          })
        }

        const response = await cbaMutation.mutateAsync({
          community_id: scenarioId,
          scenarios: cbaScenarios,
          analysis_period_years: analysisPeriod,
          discount_rate: discountRate,
          electricity_price_escalation: priceEscalation,
          // B5: Separate feed-in tariff escalation (optional — if not set, backend uses import escalation)
          feed_in_tariff_escalation: params?.feedInTariffAnnualIncreasePct != null
            ? params.feedInTariffAnnualIncreasePct / 100
            : undefined,
        })

        // The axios interceptor already unwraps .data, so `response` IS the JSON body.
        setDataSource('api')
        setResults({ ...mapBackendResponse(response, isCZK), selectedScenarios })
      } catch (error) {
        console.warn('CBA API call failed — falling back to mock data:', error.message)
        // Graceful fallback: keep the page functional even when cba-api is down.
        setDataSource('mock')
        setResults({ ...czechCBAResults({ years: params?.analysisPeriodYears || 20, currency }), selectedScenarios: ['pv_only', 'pv_bess'] })
      } finally {
        setIsLoading(false)
      }
    }

    runCBA()
    // We intentionally omit cbaMutation from the dependency array — it is
    // a stable reference from useMutation and re-including it causes loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, params?.analysisPeriodYears, params?.cbaScenarios?.join(','), currency])

  // --- Loading / empty states ---
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
        <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>
          {t('cba_results.loading')}
        </p>
      </div>
    )
  }

  if (!results) {
    return (
      <div className="text-center p-5" style={{ color: 'var(--color-neutral-600)' }}>
        {t('results.no_results_available')}
      </div>
    )
  }

  // --- Derive which scenarios are present from the results ---
  const sel = results.selectedScenarios || ['pv_only']
  const investmentScenarios = results.scenarios.slice(1)  // everything except Baseline
  const bestFinancial = investmentScenarios.reduce((a, b) => (b.npv > a.npv ? b : a), investmentScenarios[0])
  const bestEnvironmental = investmentScenarios.reduce((a, b) => (b.co2Avoided > a.co2Avoided ? b : a), investmentScenarios[0])
  const hasBattery = sel.includes('bess_only') || sel.includes('pv_bess')

  // --- Build dynamic cash flow series ---
  const scenarioColors = { 'Baseline': '#9CA3AF', 'PV Only': '#8CB808', 'BESS Only': '#2196F3', 'PV + BESS': '#5C5C5C' }
  const cashFlowSeries = [
    { name: 'Baseline', data: results.cumulativeCashFlow.baseline },
    ...(sel.includes('pv_only') && results.cumulativeCashFlow.pvOnly?.length > 0
      ? [{ name: 'PV Only', data: results.cumulativeCashFlow.pvOnly }] : []),
    ...(sel.includes('bess_only') && results.cumulativeCashFlow.bessOnly?.length > 0
      ? [{ name: 'BESS Only', data: results.cumulativeCashFlow.bessOnly }] : []),
    ...(sel.includes('pv_bess') && results.cumulativeCashFlow.pvBess?.length > 0
      ? [{ name: 'PV + BESS', data: results.cumulativeCashFlow.pvBess }] : []),
  ]
  const cfColors = cashFlowSeries.map(s => scenarioColors[s.name] || '#666')

  const cashFlowChartOptions = {
    chart: { type: 'line', height: 350, toolbar: { show: true }, animations: { enabled: true, speed: 800 } },
    colors: cfColors,
    stroke: { width: cashFlowSeries.map((_, i) => i === 0 ? 2 : 3), curve: 'smooth', dashArray: cashFlowSeries.map((_, i) => i === 0 ? 5 : 0) },
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'left' },
    xaxis: { categories: results.cumulativeCashFlow.years, title: { text: t('results.year') } },
    yaxis: { title: { text: t('cba_results.ccf_axis', { currency }) }, labels: { formatter: (value) => value != null ? fmtK(value) : '' } },
    markers: { size: 0, hover: { size: 6 } },
    tooltip: { y: { formatter: (value) => value != null ? fmtV(value) : '' } },
    grid: { borderColor: '#e0e0e0', strokeDashArray: 3 },
    annotations: { yaxis: [{ y: 0, borderColor: '#000', strokeDashArray: 2, label: { text: t('results.break_even'), style: { color: '#000', fontSize: '10px' } } }] },
  }

  // --- Build scenario options for cost breakdown dropdown ---
  const breakdownScenarioOptions = [
    { key: 'baseline', label: 'Baseline' },
    ...(sel.includes('pv_only') ? [{ key: 'pvOnly', label: 'PV Only' }] : []),
    ...(sel.includes('bess_only') ? [{ key: 'bessOnly', label: 'BESS Only' }] : []),
    ...(sel.includes('pv_bess') ? [{ key: 'pvBess', label: 'PV + BESS' }] : []),
  ]

  // Auto-select first investment scenario if not yet chosen
  const effectiveBreakdownScenario = breakdownScenario
    || (breakdownScenarioOptions.find(o => o.key !== 'baseline')?.key)
    || 'baseline'

  // --- Dynamic sensitivity columns ---
  const sensitivityColumns = [
    ...(sel.includes('pv_only') ? [{ key: 'pvOnly', label: 'PV Only', color: 'var(--color-primary)' }] : []),
    ...(sel.includes('bess_only') ? [{ key: 'bessOnly', label: 'BESS Only', color: '#2196F3' }] : []),
    ...(sel.includes('pv_bess') ? [{ key: 'pvBess', label: 'PV + BESS', color: 'var(--color-accent)' }] : []),
  ]

  return (
    <div>
      {/* Page Header */}
      <div className="tool-page-header mb-4">
        <h2 className="tool-page-title">{t('cba_results.header_title')}</h2>
        <p className="tool-page-subtitle">
          {t('cba_results.header_subtitle', { name: params?.scenarioName || t('cba_results.default_scenario_name') })}
        </p>
      </div>

      {dataSource === 'mock' && (
        <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>
            <strong>{t('cba_results.sample_data_label')}</strong> {t('cba_results.sample_data_text')}
          </div>
        </div>
      )}

      {/* Scenario Comparison Cards */}
      <Row className="g-3 mb-4">
        {results.scenarios.map((scenario, idx) => {
          const isBaseline = idx === 0
          const isBest = !isBaseline && scenario === bestFinancial
          // Color scheme per scenario name
          const colorMap = { 'Baseline': { border: 'var(--color-neutral-300)', bg: 'var(--color-neutral-100)', text: 'var(--color-neutral-700)' },
            'PV Only': { border: 'var(--color-primary)', bg: 'var(--color-primary)', text: '#fff' },
            'BESS Only': { border: '#2196F3', bg: '#2196F3', text: '#fff' },
            'PV + BESS': { border: 'var(--color-accent)', bg: 'var(--color-accent)', text: '#fff' } }
          const colors = colorMap[scenario.name] || colorMap['PV Only']
          const totalCards = results.scenarios.length
          const colSize = totalCards <= 2 ? 6 : totalCards === 3 ? 4 : 3
          return (
            <Col key={idx} md={colSize}>
              <Card style={{ border: `2px solid ${colors.border}`, borderRadius: 'var(--radius-lg)', height: '100%', overflow: 'hidden' }}>
                <div style={{ backgroundColor: colors.bg, color: colors.text, padding: '10px 16px', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                  {scenario.name}
                  {isBest && investmentScenarios.length > 1 && <span style={{ marginLeft: 8, fontSize: 'var(--font-size-xs)', opacity: 0.9 }}>{t('results.best_roi')}</span>}
                  {!isBaseline && scenario === bestEnvironmental && scenario !== bestFinancial && <span style={{ marginLeft: 8, fontSize: 'var(--font-size-xs)', opacity: 0.9 }}>{t('cba_results.badge_best_co2')}</span>}
                </div>
                <Card.Body>
                  <div className="d-flex flex-column gap-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: 'var(--color-neutral-600)' }}>{t('results.npv_colon')}</span>
                      <span style={{ fontWeight: 700, color: scenario.npv > 0 ? 'var(--color-success)' : 'var(--color-neutral-700)' }}>
                        {isBaseline ? t('results.baseline') : `${scenario.npv.toLocaleString()} ${sym}`}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: 'var(--color-neutral-600)' }}>{t('results.bcr_colon')}</span>
                      <span style={{ fontWeight: 600 }}>{isBaseline ? '—' : scenario.bcr.toFixed(1)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: 'var(--color-neutral-600)' }}>{t('results.payback_colon')}</span>
                      <span style={{ fontWeight: 600 }}>
                        {isBaseline ? '—' : scenario.payback ? t('results.payback_years', { years: scenario.payback.toFixed(1) }) : t('results.na')}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ borderTop: '1px solid var(--color-neutral-200)', paddingTop: 'var(--space-2)', marginTop: 'var(--space-1)' }}>
                      <span style={{ color: 'var(--color-neutral-600)' }}>
                        {isBaseline ? t('results.annual_grid_cost_colon') : t('results.annual_savings_colon')}
                      </span>
                      <span style={{ fontWeight: 600 }}>
                        {isBaseline
                          ? `${(scenario.annualGridCost || 0).toLocaleString()} ${sym}`
                          : `${scenario.annualSavings.toLocaleString()} ${sym}`}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ color: 'var(--color-neutral-600)' }}>{t('results.co2_avoided_colon')}</span>
                      <span style={{ fontWeight: 600, color: scenario.co2Avoided > 0 ? 'var(--color-primary)' : 'inherit' }}>
                        {isBaseline ? '—' : t('results.t_per_year', { value: scenario.co2Avoided.toFixed(1) })}
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )
        })}
      </Row>

      {/* Charts Row 1: Cumulative Cash Flow + Sensitivity */}
      <Row className="g-4 mb-4">
        <Col md={8}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                {t('results.cumulative_cash_flow')}
              </h6>
              <Chart
                options={cashFlowChartOptions}
                series={cashFlowSeries}
                type="line"
                height={350}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)', height: '100%' }}>
            <Card.Body>
              <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                {t('results.sensitivity_analysis')}
              </h6>
              <table className="mb-0" style={{ width: '100%', fontSize: 'var(--font-size-xs)', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-neutral-100)', borderBottom: '2px solid var(--color-neutral-300)' }}>
                    <th style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--color-neutral-700)' }}>{t('results.discount_rate')}</th>
                    {sensitivityColumns.map(col => (
                      <th key={col.key} style={{ padding: '8px 10px', fontWeight: 600, color: col.color, textAlign: 'right' }}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.sensitivity.discountRates.map((rate, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--color-neutral-200)' }}>
                      <td style={{ padding: '6px 10px', color: 'var(--color-neutral-700)' }}>{rate}%</td>
                      {sensitivityColumns.map(col => (
                        <td key={col.key} style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500 }}>{fmtK(results.sensitivity[col.key]?.[idx])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--color-neutral-700)' }}>{t('results.how_to_read_label')}</strong> {t('cba_results.how_to_read_text')}
                {(() => {
                  const rates = results.sensitivity.discountRates || []
                  const mainCol = sensitivityColumns[0]
                  if (!mainCol || rates.length < 2) return null
                  const vals = results.sensitivity[mainCol.key] || []
                  const lowIdx = 0
                  const highIdx = rates.length - 1
                  const allPositive = vals.every(v => v > 0)
                  const allNegative = vals.every(v => v < 0)
                  const crossesZero = vals.some(v => v > 0) && vals.some(v => v < 0)
                  return (
                    <span>
                      {' '}{t('cba_results.sensitivity_detail', { lowRate: rates[lowIdx], label: mainCol.label, lowVal: fmtK(vals[lowIdx]), highRate: rates[highIdx], highVal: fmtK(vals[highIdx]) })}{' '}
                      {allPositive && t('cba_results.sensitivity_all_positive')}
                      {allNegative && t('cba_results.sensitivity_all_negative')}
                      {crossesZero && t('cba_results.sensitivity_crosses_zero')}
                    </span>
                  )
                })()}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2: Cumulative Cost vs Benefits + CO2 Impact */}
      <Row className="g-4 mb-4">
        <Col md={8}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 'var(--space-3)' }}>
                <h6 style={{ fontWeight: 600, margin: 0 }}>
                  {t('results.costs_vs_benefits')}
                </h6>
                <select
                  value={effectiveBreakdownScenario}
                  onChange={(e) => setBreakdownScenario(e.target.value)}
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-neutral-300)',
                    backgroundColor: 'var(--color-neutral-50)',
                    color: 'var(--color-neutral-700)',
                    cursor: 'pointer',
                  }}
                >
                  {breakdownScenarioOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {(() => {
                const bd = results.annualCostBreakdown[effectiveBreakdownScenario] || {}
                const hasBatt = (bd.batteryValue || []).some(v => v > 0)
                const hasExport = (bd.exportRevenue || []).some(v => v > 0)
                const hasReplacement = (bd.replacementCost || []).some(v => v > 0)
                const hasCapex = (bd.capex || 0) > 0
                const numCategories = (results.annualCostBreakdown.categories || []).length

                // CAPEX is a one-time cost at year 1, shown as negative in the first bar only
                const capexData = numCategories > 0 ? [-(bd.capex || 0), ...Array(numCategories - 1).fill(0)] : []

                // Costs shown as negative (below zero), benefits as positive (above zero)
                const series = [
                  ...(hasCapex ? [{ name: t('results.capex_series'), data: capexData }] : []),
                  { name: t('results.grid_import'), data: (bd.gridImport || []).map(v => -v) },
                  { name: t('results.om_series'), data: (bd.om || []).map(v => -v) },
                  ...(hasReplacement ? [{ name: t('results.battery_replacement'), data: (bd.replacementCost || []).map(v => -v) }] : []),
                  { name: t('results.pv_self_consumption_savings'), data: bd.pvValue || [] },
                  ...(hasBatt ? [{ name: t('results.battery_savings'), data: bd.batteryValue || [] }] : []),
                  ...(hasExport ? [{ name: t('results.export_revenue'), data: bd.exportRevenue || [] }] : []),
                ]
                const costColors = []
                if (hasCapex) costColors.push('#7B2D8E')
                costColors.push('#DC3545', '#9CA3AF')
                if (hasReplacement) costColors.push('#FF8C00')
                const benefitColors = ['#8CB808']
                if (hasBatt) benefitColors.push('#2196F3')
                if (hasExport) benefitColors.push('#66BB6A')
                const colors = [...costColors, ...benefitColors]

                const options = {
                  chart: { type: 'bar', height: 350, stacked: true, toolbar: { show: false } },
                  colors,
                  plotOptions: { bar: { horizontal: false, columnWidth: '65%' } },
                  dataLabels: { enabled: false },
                  legend: { position: 'top', horizontalAlign: 'left', fontSize: '11px' },
                  xaxis: { categories: results.annualCostBreakdown.categories },
                  yaxis: {
                    title: { text: t('cba_results.cumulative_axis', { currency }) },
                    labels: { formatter: (value) => value != null ? `${(value / 1000).toFixed(0)}k` : '' },
                  },
                  tooltip: { y: { formatter: (value) => value != null ? `${Math.abs(value).toLocaleString()} ${sym}` : '' } },
                  fill: { opacity: 0.85 },
                  annotations: { yaxis: [{ y: 0, borderColor: '#333', strokeDashArray: 0, borderWidth: 1 }] },
                }

                return <Chart options={options} series={series} type="bar" height={350} />
              })()}
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: 'var(--space-2)' }}>
                {t('cba_results.breakdown_note')}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)', height: '100%' }}>
            <Card.Body>
              {(() => {
                const co2Options = investmentScenarios.map((s, i) => ({ idx: i, ...s }))
                const selectedIdx = co2Scenario != null ? co2Scenario : co2Options.findIndex(s => s === bestEnvironmental)
                const sc = co2Options[selectedIdx >= 0 ? selectedIdx : 0]
                if (!sc) return null
                const isBest = sc === bestEnvironmental || sc.name === bestEnvironmental?.name
                const lifetimeCO2 = sc.lifetimeCO2Avoided != null
                  ? sc.lifetimeCO2Avoided
                  : sc.co2Avoided * (params?.analysisPeriodYears || 20)
                const trees = sc.equivalentTreesPlanted != null
                  ? sc.equivalentTreesPlanted
                  : lifetimeCO2 * 40

                return (
                  <>
                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 'var(--space-3)' }}>
                      <h6 style={{ fontWeight: 600, margin: 0 }}>{t('results.co2_impact')}</h6>
                      <select
                        value={selectedIdx}
                        onChange={(e) => setCo2Scenario(Number(e.target.value))}
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          padding: '4px 10px',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--color-neutral-300)',
                          backgroundColor: 'var(--color-neutral-50)',
                          color: 'var(--color-neutral-700)',
                          cursor: 'pointer',
                        }}
                      >
                        {co2Options.map((opt, i) => (
                          <option key={i} value={i}>{opt.name}</option>
                        ))}
                      </select>
                    </div>
                    {isBest && investmentScenarios.length > 1 && (
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                        {t('cba_results.best_env_badge')}
                      </div>
                    )}
                    <div className="d-flex flex-column gap-3">
                      <div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.lifetime_co2_avoided')}</div>
                        <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-success)' }}>
                          {t('results.tons', { value: lifetimeCO2.toFixed(0) })}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.annual_co2_reduction')}</div>
                        <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
                          {t('results.tons_per_year', { value: sc.co2Avoided.toFixed(1) })}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('results.equivalent_trees')}</div>
                        <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
                          {t('results.trees_count', { count: Math.round(trees).toLocaleString() })}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: '4px' }}>
                          {t('results.trees_note')}
                        </div>
                      </div>
                    </div>
                  </>
                )
              })()}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Summary Recommendation */}
      <Card
        style={{
          border: '1px solid var(--color-primary-light)',
          borderRadius: 'var(--radius-lg)',
          backgroundColor: 'var(--color-primary-pale)',
        }}
        className="mb-4"
      >
        <Card.Body>
          <h6 style={{ fontWeight: 600, color: 'var(--color-primary-dark)', marginBottom: 'var(--space-3)' }}>
            {t('results.recommendation_summary')}
          </h6>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-800)' }}>
            <p style={{ marginBottom: 'var(--space-2)' }}>
              <strong>{t('results.best_financial_return')}</strong> {bestFinancial.name}
            </p>
            <p style={{ marginBottom: 'var(--space-2)' }}>
              {t('cba_results.best_financial_detail', {
                name: bestFinancial.name,
                npv: bestFinancial.npv.toLocaleString(),
                sym,
                bcr: bestFinancial.bcr.toFixed(1),
                payback: bestFinancial.payback?.toFixed(1) ?? t('results.na'),
                savings: bestFinancial.annualSavings.toLocaleString(),
              })}
            </p>
            {bestEnvironmental !== bestFinancial && (
              <p style={{ marginBottom: 'var(--space-2)' }}>
                <strong>{t('results.best_environmental_impact')}</strong> {t('cba_results.best_env_detail', {
                  name: bestEnvironmental.name,
                  co2: bestEnvironmental.co2Avoided.toFixed(1),
                  more: (bestFinancial.co2Avoided > 0 && bestEnvironmental.co2Avoided > bestFinancial.co2Avoided)
                    ? t('cba_results.best_env_more', { pct: ((bestEnvironmental.co2Avoided / bestFinancial.co2Avoided - 1) * 100).toFixed(0), name: bestFinancial.name })
                    : '',
                  payback: bestEnvironmental.payback?.toFixed(1) ?? t('results.na'),
                })}
              </p>
            )}
            <p style={{ marginBottom: 0 }}>
              <strong>{t('cba_results.key_risk_factors')}</strong> {t('cba_results.key_risk_text', {
                battery: (hasBattery && params?.battLifetimeYears)
                  ? t('cba_results.key_risk_battery', { year: params.battLifetimeYears })
                  : '',
              })}
            </p>
          </div>
        </Card.Body>
      </Card>

      {/* Actions */}
      {saveStatus === 'success' && (
        <Alert variant="success" className="mb-3 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
          {t('cba_results.save_success')}
        </Alert>
      )}
      {saveStatus === 'error' && (
        <Alert variant="danger" className="mb-3 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
          {t('results.save_failed')}
        </Alert>
      )}
      <div className="d-flex gap-2 justify-content-end">
        <Button variant="outline-secondary" onClick={handleExportCSV} disabled={!results}>
          {t('results.export_csv_data')}
        </Button>
        {canEdit && (
        <Button
          variant="primary"
          style={{
            backgroundColor: 'var(--color-primary)',
            borderColor: 'var(--color-primary)',
          }}
          onClick={handleSaveResults}
          disabled={isSaving || !results}
        >
          {isSaving ? (
            <><Spinner animation="border" size="sm" className="me-1" />{t('results.saving')}</>
          ) : t('results.save_results')}
        </Button>
        )}
      </div>
    </div>
  )
}

export default CBAResultsPage
